// src/routes/AppRoutes.js

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Typography } from '@mui/material';

import DashboardPage from '../pages/admin/DashboardPage';
import CamerasPage from '../pages/admin/CamerasPage';
import CameraPage from '../pages/admin/CameraPage';
import ClientDirectoryPage from '../pages/admin/ClientDirectoryPage';
import ClientWorkspacePage from '../pages/admin/ClientWorkspacePage';
import RelativesPage from '../pages/admin/RelativesPage';
import ServiceTypesPage from '../pages/admin/ServiceTypesPage';
import VideosPage from '../pages/admin/VideosPage';
import InvoicesPage from '../pages/admin/InvoicesPage';
import SubscriptionsPage from '../pages/admin/Subscriptions';
import PaymentsPage from '../pages/admin/PaymentsPage';
import ProfilePage from '../pages/admin/ProfilePage';
import ContactSubmissionsPage from '../pages/admin/ContactSubmissionsPage';
import FallEventsPage from '../pages/admin/FallEventsPage';
import FallReportsPage from '../pages/admin/FallReportsPage';
import MonitoringWorkspacePage from '../pages/admin/MonitoringWorkspacePage';
import UserActivityPage from '../pages/admin/UserActivityPage';
import StaffPage from '../pages/admin/StaffPage';
import ClientDashboardPage from '../pages/client/ClientDashboardPage';
import NurseDashboard from '../pages/nurse/NurseDashboard';
import ClientInvoicesPage from '../pages/client/ClientInvoicesPage';
import CarerDashboard from '../pages/carer/CarerDashboard';
import ClientProfile from '../pages/client/ClientProfile';
import FallLogsPage from '../pages/shared/FallLogsPage';
import ContactPage from '../pages/shared/ContactPage';
import Verify2FA from '../pages/shared/Verify2FA';
import LandingPage from '../pages/shared/LandingPage';
import AboutUs from '../pages/shared/AboutUs';
import ServicesPage from '../pages/shared/ServicesPage';
import LoginPage from '../pages/shared/LoginPage';
import RegisterPage from '../pages/shared/RegisterPage';
import ForgotPasswordPage from '../pages/shared/ForgotPasswordPage';
import ResetPasswordPage from '../pages/shared/ResetPasswordPage';
import ConfirmEmailChangePage from '../pages/shared/ConfirmEmailChangePage';
import { useAuth } from '../contexts/AuthContext';

function PrivateRoute({ element }) {
  const { user, loading } = useAuth();
  if (loading) return <Typography>Loading...</Typography>;
  return user ? element : <Navigate to="/login" />;
}

// Pick the right dashboard for the logged-in user's role.
// Used for `/` when someone hits the root URL while authenticated.
function RoleHomeRedirect() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === 'client') return <Navigate to="/client-dashboard" replace />;
  if (role === 'nurse') return <Navigate to="/nurse-dashboard" replace />;
  if (role === 'carer') return <Navigate to="/carer-dashboard" replace />;
  // admin + any other role default to the main dashboard
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes({ isPrivate = false }) {
  if (!isPrivate) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/verify" element={<Verify2FA />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<RoleHomeRedirect />} />
      <Route path="/dashboard" element={<PrivateRoute element={<DashboardPage />} />} />
      <Route path="/client-dashboard" element={<PrivateRoute element={<ClientDashboardPage />} />} />
      <Route path="/nurse-dashboard" element={<PrivateRoute element={<NurseDashboard />} />} />
      <Route path="/carer-dashboard" element={<PrivateRoute element={<CarerDashboard />} />} />
      <Route path="/profile" element={<PrivateRoute element={<ProfilePage />} />} />
      <Route path="/client-profile" element={<PrivateRoute element={<ClientProfile />} />} />

      <Route path="/cameras" element={<PrivateRoute element={<CamerasPage />} />} />
      <Route path="/camera" element={<PrivateRoute element={<CameraPage />} />} />
      <Route path="/clients" element={<PrivateRoute element={<ClientDirectoryPage />} />} />
      <Route path="/staff" element={<PrivateRoute element={<StaffPage />} />} />
      <Route path="/clients/:clientId" element={<PrivateRoute element={<ClientWorkspacePage />} />} />
      <Route path="/monitoring" element={<PrivateRoute element={<MonitoringWorkspacePage />} />} />
      <Route path="/relatives" element={<PrivateRoute element={<RelativesPage />} />} />
      <Route path="/service-types" element={<PrivateRoute element={<ServiceTypesPage />} />} />
      <Route path="/videos" element={<PrivateRoute element={<VideosPage />} />} />
      <Route path="/fall-events" element={<PrivateRoute element={<FallEventsPage />} />} />
      <Route path="/fall-reports" element={<PrivateRoute element={<FallReportsPage />} />} />
      <Route path="/contact-submissions" element={<PrivateRoute element={<ContactSubmissionsPage />} />} />
      <Route path="/user-activity" element={<PrivateRoute element={<UserActivityPage />} />} />
      <Route path="/fall-logs" element={<PrivateRoute element={<FallLogsPage />} />} />
      <Route path="/contact" element={<PrivateRoute element={<ContactPage />} />} />

      <Route path="/invoices" element={<PrivateRoute element={<InvoicesPage />} />} />
      <Route path="/payments" element={<PrivateRoute element={<PaymentsPage />} />} />
      <Route path="/subscriptions" element={<PrivateRoute element={<SubscriptionsPage />} />} />
      <Route path="/client-invoices" element={<PrivateRoute element={<ClientInvoicesPage />} />} />

      <Route path="*" element={<Typography>Page not found.</Typography>} />
    </Routes>
  );
}

export default AppRoutes;
